import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Zap } from "lucide-react";
import { publicApi, useAccount } from "@/lib/account";
import { payWithRazorpay } from "@/lib/razorpay";
import { usePrefs } from "@/lib/prefs";

const durationOf = (d, t) => (d === 1 ? t("day") : d === 7 ? t("week") : d === 30 ? t("month") : `${d / 30} ${t("months")}`);

export default function PlansGrid() {
  const [plans, setPlans] = useState([]);
  const { isSignedIn, authed, profile, refresh, account } = useAccount();
  const { t, local } = usePrefs();
  const duration = (d) => durationOf(d, t);
  const feats = (p) => t(`feat_${p.id}`) || p.features;
  const navigate = useNavigate();
  useEffect(() => { publicApi.get("/plans").then((r) => setPlans(r.data.plans)); }, []);

  const subscribe = (p) => {
    if (!isSignedIn) return navigate("/sign-in");
    payWithRazorpay({ authed, payload: { kind: "plan", plan_id: p.id }, description: `${t(`name_${p.id}`)} plan`, prefillName: profile?.name, onSuccess: refresh });
  };
  const label = (p) => (account?.plan_active && account.plan === p.id ? t("extend") : p.trial ? t("startTrial") : t("subscribe"));
  const Local = ({ p }) => local(p.final_price) && <p className="text-xs text-slate-400 mt-1" data-testid={`plan-local-price-${p.id}`}>{local(p.final_price)}</p>;
  const trials = plans.filter((p) => p.trial);
  const main = plans.filter((p) => !p.trial);

  return (
    <div data-testid="plans-grid">
      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        {trials.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className="card-royal rounded-2xl p-6 flex items-center gap-5 bg-gradient-to-r from-[#0F382C]/60 to-[#181B26]">
            <Zap className="w-8 h-8 text-emerald-300 shrink-0" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold">{t(`name_${p.id}`)}</div>
              <div className="font-display text-4xl font-semibold mt-1">{p.final_price < p.price && <s className="text-xl text-slate-500 mr-2" data-testid={`plan-original-price-${p.id}`}>₹{p.price}</s>}<span data-testid={`plan-price-${p.id}`}>₹{p.final_price}</span><span className="text-sm text-slate-400 font-sans"> / {duration(p.days)}</span></div>
              <Local p={p} />
              <p className="text-xs text-slate-400 mt-1">{feats(p).slice(0, 2).join(" · ")}</p>
            </div>
            <button onClick={() => subscribe(p)} data-testid={`plan-subscribe-${p.id}-button`} className="btn-gold px-5 py-2.5 rounded-full text-sm font-medium shrink-0">{label(p)}</button>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {main.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`}
            className={`card-royal rounded-3xl p-8 flex flex-col relative ${p.popular ? "md:-translate-y-4 border-amber-400/60 bg-gradient-to-b from-[#2A1A10] to-[#181B26]" : ""}`}>
            {p.popular && <span className="absolute -top-3 left-8 px-3 py-1 text-xs rounded-full btn-gold font-medium">{t("bestValue")}</span>}
            <div className="flex items-center gap-2 text-amber-300"><Crown className="w-5 h-5" /><span className="uppercase tracking-[0.2em] text-xs font-semibold">{t(`name_${p.id}`)}</span></div>
            <div className="mt-6 flex items-baseline gap-2">
              {p.final_price < p.price && <s className="text-2xl text-slate-500" data-testid={`plan-original-price-${p.id}`}>₹{p.price}</s>}
              <span className="font-display text-5xl font-semibold" data-testid={`plan-price-${p.id}`}>₹{p.final_price}</span>
              <span className="text-slate-400 text-sm">/ {duration(p.days)}</span>
            </div>
            <Local p={p} />
            <p className="text-xs text-emerald-300 mt-2">{t("perDay").replace("{x}", (p.final_price / p.days).toFixed(1))}</p>
            <ul className="mt-8 space-y-3 flex-1">
              {feats(p).map((f) => <li key={f} className="flex gap-3 text-sm text-slate-300"><Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            <button onClick={() => subscribe(p)} data-testid={`plan-subscribe-${p.id}-button`}
              className={`${p.popular ? "btn-gold" : "btn-ghost"} mt-10 py-3 rounded-full font-medium`}>{label(p)}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
